import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Snackbar,
  Alert,
  LinearProgress,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SwapHoriz as SwapIcon,
  ContentCopy as CopyIcon,
  PeopleAlt as PeopleIcon,
  Shield as ShieldIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { jwtDecode } from 'jwt-decode';
import { setToken } from '../../components/jwtToken';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';
import { alpha } from '@mui/material/styles';

const BRAND = PRIMARYCOLOR;

const emailFromToken = (token) => {
  try {
    const decoded = jwtDecode(token);
    // common claims your backend might use
    return decoded.username || decoded.email || decoded.sub || '';
  } catch {
    return '';
  }
};

const teamIdFromToken = (token) => {
  try {
    const decoded = jwtDecode(token);
    return Number(decoded.team_id);
  } catch {
    return null;
  }
};

// Pretty avatar colors from a string
const stringToColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i += 1) {
    // eslint-disable-next-line no-bitwise
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

const initials = (emailOrName) => {
  if (!emailOrName) return '??';
  const name = emailOrName.split('@')[0];
  const parts = name.replace(/[^a-zA-Z0-9 ]/g, ' ').trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const roleChipColor = (role) => {
  switch ((role || '').toLowerCase()) {
    case 'owner':
      return 'primary';
    case 'admin':
      return 'warning';
    default:
      return 'default';
  }
};

const membershipColor = (membership) => {
  switch ((membership || '').toLowerCase()) {
    case 'beta':
      return 'info';
    case 'pro':
      return 'success';
    case 'free':
      return 'default';
    default:
      return 'default';
  }
};

const Teams = ({ embedded = false }) => {
  const token = sessionStorage.getItem('token');
  const me = emailFromToken(token);

  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [teamUsers, setTeamUsers] = useState([]);
  const [isOwner, setIsOwner] = useState(false);

  // UI state
  const [addOpen, setAddOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [newUserEmail, setNewUserEmail] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [userToDelete, setUserToDelete] = useState(null);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const [toast, setToast] = useState({ open: false, severity: 'success', msg: '' });

  // ---------- Data fetching ----------
  const fetchTeams = async () => {
    try {
      const res = await fetch(`${BASEURL}/teams`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success === 1 && Array.isArray(data.teams)) {
        setTeams(data.teams);
        return data.teams;
      }
      setTeams([]);
      return [];
    } catch (e) {
      setTeams([]);
      throw e;
    }
  };

  const fetchTeamUsers = async (teamId) => {
    try {
      const res = await fetch(`${BASEURL}/team/users?team_id=${teamId}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success === 1 && Array.isArray(data.users)) {
        setTeamUsers(data.users);
        const owner = data.users.find((u) => u.role === 'owner' && u.email === me);
        setIsOwner(Boolean(owner));
      } else {
        setTeamUsers([]);
        setIsOwner(false);
      }
    } catch {
      setTeamUsers([]);
      setIsOwner(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const ts = await fetchTeams();
      if (ts.length) {
        const initialId = teamIdFromToken(token);
        const ct = ts.find((t) => t.id === initialId) || ts[0];
        setCurrentTeam(ct);
        if (ct.users && Array.isArray(ct.users)) {
          setTeamUsers(ct.users);
          const owner = ct.users.find((u) => u.role === 'owner' && u.email === me);
          setIsOwner(Boolean(owner));
        } else {
          await fetchTeamUsers(ct.id);
        }
      } else {
        setCurrentTeam(null);
        setTeamUsers([]);
        setIsOwner(false);
      }
    } catch (e) {
      setToast({ open: true, severity: 'error', msg: 'Failed to load teams.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ---------- Actions ----------
  const addUser = async () => {
    try {
      const res = await fetch(`${BASEURL}/team/user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ team_id: currentTeam.id, email: newUserEmail })
      });
      if (!res.ok) throw new Error();
      setAddOpen(false);
      setNewUserEmail('');
      await load();
      setToast({ open: true, severity: 'success', msg: 'User invited/added.' });
    } catch {
      setToast({ open: true, severity: 'error', msg: 'Could not add user.' });
    }
  };

  const confirmDelete = (user) => {
    setUserToDelete(user);
    setDeleteOpen(true);
  };

  const removeUser = async () => {
    try {
      await fetch(`${BASEURL}/team/user?team_id=${currentTeam.id}&email=${userToDelete.email}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteOpen(false);
      setUserToDelete(null);
      await load();
      setToast({ open: true, severity: 'success', msg: 'User removed.' });
    } catch {
      setToast({ open: true, severity: 'error', msg: 'Could not remove user.' });
    }
  };

  const switchTeam = async (teamId) => {
    try {
      const res = await fetch(`${BASEURL}/team/switch?team_id=${teamId}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.token) {
        setToken(data.token);
        await load();
        setToast({ open: true, severity: 'success', msg: 'Switched team.' });
      } else {
        throw new Error();
      }
    } catch {
      setToast({ open: true, severity: 'error', msg: 'Could not switch team.' });
    }
  };

  const updateTeamName = async () => {
    try {
      const res = await fetch(`${BASEURL}/team/name`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ team_id: currentTeam.id, new_team_name: newTeamName })
      });
      const data = await res.json();
      if (data.success === 1) {
        setRenameOpen(false);
        setNewTeamName('');
        await load();
        setToast({ open: true, severity: 'success', msg: 'Team name updated.' });
      } else {
        throw new Error();
      }
    } catch {
      setToast({ open: true, severity: 'error', msg: 'Could not update team name.' });
    }
  };

  // ---------- Derived ----------
  const filteredUsers = useMemo(() => {
    let list = teamUsers || [];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          (u.role || '').toLowerCase().includes(q)
      );
    }
    if (roleFilter !== 'all') {
      list = list.filter((u) => (u.role || '').toLowerCase() === roleFilter);
    }
    return list;
  }, [teamUsers, search, roleFilter]);

  const adminCount = useMemo(
    () => (teamUsers || []).filter((u) => (u.role || '').toLowerCase() === 'admin').length,
    [teamUsers]
  );

  const isCurrentTeam = (team) => currentTeam && team.id === currentTeam.id;

  // ---------- UI ----------
  return (
    <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
            position: 'relative',
            overflow: 'hidden',
            mb: 2.4,
            borderRadius: 5,
            border: `1px solid ${alpha(BRAND, 0.14)}`,
            bgcolor: '#ffffff',
            boxShadow: `0 18px 42px ${alpha(BRAND, 0.08)}`,
          }}
      >
        <Box aria-hidden sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, bgcolor: BRAND }} />
        <Box aria-hidden sx={{ position: 'absolute', top: -90, right: -30, width: 260, height: 260, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(BRAND, 0.14)} 0%, rgba(255,255,255,0) 72%)` }} />
        <Box sx={{ position: 'relative', p: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} justifyContent="space-between">
          <Box>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: BRAND }}>
              {embedded ? 'Team Settings' : 'Team'}
            </Typography>
            <Typography variant={embedded ? 'h4' : 'h3'} sx={{ mt: 0.8, fontWeight: 900, letterSpacing: '-0.03em', color: '#0f172a', fontSize: embedded ? { xs: '1.65rem', md: '2rem' } : { xs: '2rem', md: '2.45rem' }, lineHeight: 1.05 }}>
              Team Workspace
            </Typography>
            <Typography sx={{ mt: 1, maxWidth: 560, fontSize: '0.98rem', lineHeight: 1.6, color: alpha('#0f172a', 0.68) }}>
              Manage access, roles, and team membership from one place.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.6 }} useFlexGap flexWrap="wrap">
              <Typography variant="subtitle1" sx={{ color: alpha('#0f172a', 0.72), fontWeight: 700 }}>
                Current:
              </Typography>
              <Chip
                label={currentTeam ? currentTeam.name : '—'}
                variant="filled"
                color="default"
                sx={{ bgcolor: alpha(BRAND, 0.08), color: BRAND, border: `1px solid ${alpha(BRAND, 0.14)}`, fontWeight: 800 }}
              />
              {currentTeam?.membership && (
                <Chip
                  size="small"
                  label={currentTeam.membership}
                  color={membershipColor(currentTeam.membership)}
                  variant="outlined"
                  sx={{ fontWeight: 800 }}
                />
              )}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="team-switcher">Switch Team</InputLabel>
              <Select
                labelId="team-switcher"
                value={currentTeam?.id || ''}
                label="Switch Team"
                onChange={(e) => switchTeam(e.target.value)}
                sx={{
                  bgcolor: '#ffffff',
                  borderRadius: 2.5,
                  '.MuiOutlinedInput-notchedOutline': { borderColor: alpha('#0f172a', 0.14) },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(BRAND, 0.45) },
                }}
              >
                {teams.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name} {isCurrentTeam(t) ? ' (Current)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isOwner && (
              <>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={() => setAddOpen(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    borderRadius: 2.5,
                    px: 2.4,
                    py: 1.15,
                  }}
                >
                  Add User
                </Button>
                <Button
                  startIcon={<EditIcon />}
                  variant="outlined"
                  onClick={() => setRenameOpen(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    borderRadius: 2.5,
                    px: 2.4,
                    py: 1.15,
                    bgcolor: '#ffffff',
                    borderColor: alpha(BRAND, 0.35),
                    color: BRAND,
                    '&:hover': { bgcolor: alpha(BRAND, 0.06), borderColor: BRAND },
                  }}
                >
                  Rename Team
                </Button>
              </>
            )}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 1.6, flexWrap: 'wrap' }} useFlexGap>
          <Chip
            icon={<PeopleIcon />}
            label={`Members: ${teamUsers.length}`}
            variant="outlined"
            sx={{ borderColor: alpha(BRAND, 0.18), color: BRAND, fontWeight: 800, '& .MuiChip-icon': { color: BRAND } }}
          />
          <Chip
            icon={<ShieldIcon />}
            label={`Admins: ${adminCount}`}
            variant="outlined"
            sx={{ borderColor: alpha('#16a34a', 0.2), color: '#15803d', fontWeight: 800, '& .MuiChip-icon': { color: '#15803d' } }}
          />
        </Stack>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Team Users */}
        <Grid item xs={12} md={7}>
          <Card component={Paper} elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Team Users
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  placeholder="Search users or roles…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="role-filter">Role</InputLabel>
                  <Select
                    labelId="role-filter"
                    value={roleFilter}
                    label="Role"
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="owner">Owner</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="member">Member</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {filteredUsers.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body1">No users match your filters.</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((u) => (
                      <TableRow key={u.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              sx={{ bgcolor: stringToColor(u.email), width: 32, height: 32, fontSize: 14 }}
                            >
                              {initials(u.email)}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 600 }}>{u.email}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {u.user_id}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={u.role} color={roleChipColor(u.role)} variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Copy email">
                              <IconButton size="small" onClick={() => navigator.clipboard.writeText(u.email)}>
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {isOwner && u.role !== 'owner' && (
                              <Tooltip title="Remove user">
                                <IconButton size="small" color="error" onClick={() => confirmDelete(u)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </Grid>

        {/* All Teams */}
        <Grid item xs={12} md={5}>
          <Card component={Paper} elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                All Teams
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              {teams.map((t) => {
                const userCount = Array.isArray(t.users) ? t.users.length : '—';
                return (
                  <Grid item xs={12} key={t.id}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {t.name}
                            </Typography>
                            {t.membership && (
                              <Chip size="small" label={t.membership} color={membershipColor(t.membership)} />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" icon={<PeopleIcon />} label={`Users: ${userCount}`} variant="outlined" />
                          </Stack>
                        </Box>
                        <Box>
                          {isCurrentTeam(t) ? (
                            <Chip icon={<SwapIcon />} label="Current" color="success" />
                          ) : (
                            <Button variant="outlined" startIcon={<SwapIcon />} onClick={() => switchTeam(t.id)}>
                              Switch
                            </Button>
                          )}
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
              {teams.length === 0 && (
                <Grid item xs={12}>
                  <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography>No teams yet.</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Card>
        </Grid>
      </Grid>

      {/* Add User Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add user to team</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label="User email"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            placeholder="name@company.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={addUser} disabled={!newUserEmail}>
            Add User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Team Dialog */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Rename team</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            label="New team name"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            placeholder={currentTeam?.name || 'Team name'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={updateTeamName} disabled={!newTeamName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove User Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Remove user</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to remove <b>{userToDelete?.email}</b> from <b>{currentTeam?.name}</b>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={removeUser}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toasts */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Teams;
